import { SpinnerIcon, UpDownIcon, WarningIcon } from 'components/icons'
import { Attribution } from 'components/ui'
import { useCurrentUser } from 'context/current-user-context'
import { useOnboarding } from 'context/onboarding-context'
import classNames from 'helpers/class-names'
import getInitials from 'helpers/get-initials'
import { useFileToValidateToolLibrary } from 'hooks/use-file-to-validate-tool-library'
import { useEffect, useMemo } from 'react'
import { Tooltip } from 'ui/tooltip'

const UploadedToolLibrary = () => {
  const { success, error, fileName, uploadFile } =
    useFileToValidateToolLibrary()
  const {
    toolLibrary,
    setToolLibrary,
    toolLibraryFile,
    handleDeleteToolLibrary,
  } = useOnboarding()
  const { fullName } = useCurrentUser()
  const initials = useMemo(
    () => getInitials(toolLibrary.fileName),
    [toolLibrary.fileName],
  )
  const isLoading = !Boolean(toolLibrary?.success)
  const hasError = Boolean(toolLibrary?.error)

  useEffect(() => {
    if (toolLibraryFile) {
      uploadFile(toolLibraryFile)
    }
  }, [])

  useEffect(() => {
    if (success) {
      setToolLibrary({ success: true, fileName })
    }
  }, [success])

  useEffect(() => {
    if (error) {
      setToolLibrary({ success: false, fileName, error })
    }
  }, [error])

  return (
    <div className="mb-5 mt-6 flex flex-row items-center gap-3 rounded-lg border border-gray-100 p-4">
      {!hasError && (
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-gray-200 text-lg font-semibold text-white">
          {isLoading ? (
            <SpinnerIcon className="size-5 animate-spin " />
          ) : (
            initials
          )}
        </div>
      )}
      {hasError && (
        <div className="flex size-10 shrink-0 items-center justify-center text-danger">
          <WarningIcon className="size-9" />
        </div>
      )}
      <div className="flex flex-1 flex-col truncate">
        <span className="font-base truncate font-bold text-gray-500">
          {toolLibrary.fileName}
        </span>
        {!hasError && (
          <Attribution
            action="uploaded"
            by={fullName}
            at={new Date()}
            byClassName="font-bold"
            atClassName="font-bold"
            className="truncate"
          />
        )}
        {hasError && (
          <span className="text-xs font-semibold text-gray-300">
            {toolLibrary.error}
          </span>
        )}
      </div>
      <Tooltip
        tip={!isLoading || hasError ? 'Choose a different tool library' : null}>
        <button
          onClick={handleDeleteToolLibrary}
          disabled={isLoading && !hasError}
          className={classNames(
            'rounded-lg p-1 text-gray-200 outline-none ring-info/75 transition duration-300 ease-in-out ',
            {
              'hover:bg-gray-50 hover:text-gray-300 focus-visible:text-gray-300  focus-visible:ring-2 active:scale-90':
                !isLoading || hasError,
              'animate-pulse cursor-not-allowed bg-gray-50 text-gray-100':
                isLoading && !hasError,
            },
          )}>
          <UpDownIcon className="size-6 rotate-90" />
        </button>
      </Tooltip>
    </div>
  )
}

export default UploadedToolLibrary
